import schedulePageActions from './schedulePageActions';
import { endOfDay, format, startOfDay, parseISO, add } from 'date-fns';
import $api from 'http/index';

import { getOptions } from 'helpers/getOptions';
import { SCHEDULE_TYPES } from 'pages/ScheduleV6/constants/constants';
import { getMonthDatesByFullWeeksAsKeys } from 'helpers/_helpers';
import { isMobileSchedule } from 'helpers/_helpers';
import { globalBEDateFormat, startEndFromTravelTimeOnlyFormat } from 'common/dateFormatConfig';
import { getSheetEventTitle } from 'pages/ScheduleV6/utils';

export const startEndFromTravelTimeOnly = (createdAt, startTime) => {
  const combinedDate = parseISO(createdAt);

  combinedDate.setHours(parseInt(startTime.hour), parseInt(startTime.minute));

  return format(combinedDate, startEndFromTravelTimeOnlyFormat);
};

export const adaptedDataHelper = (data) => {
  const adaptedData = data.map((el) => ({
    _id: el._id,
    project: el.project,
    sheet: el,
    title: getSheetEventTitle(el),
    grid: el.grid,
    workers: el.workers,
    equipment: el.equipment,
    noWorkers: el.workers.length === 0,
    originalStart: el.travelTimeOnly
      ? startEndFromTravelTimeOnly(el.startTimeDate || el.createdAt, el.startTime)
      : el.hours.start,
    originalEnd: el.travelTimeOnly
      ? startEndFromTravelTimeOnly(el.startTimeDate || el.createdAt, el.startTime)
      : el.hours.end,
    start: el.travelTimeOnly
      ? startEndFromTravelTimeOnly(el.startTimeDate || el.createdAt, el.startTime) || '0'
      : el.hours.start,
    end: el.travelTimeOnly
      ? startEndFromTravelTimeOnly(el.startTimeDate || el.createdAt, el.startTime)
      : el.hours.end,
  }));
  return adaptedData;
};

export const setCrewLeader = (data) => async (dispatch, getState) => {
  const { view, sheets } = getState().schedule;
  const isClView = view === SCHEDULE_TYPES.cl;

  try {
    if (!isClView) return dispatch(schedulePageActions.setCrewLeaderAction(data));

    dispatch(updateSheets());
  } catch (e) {
    console.log(e);
  }
};

export const getCalendarEvents = (date) => async (dispatch) => {
  try {
    const response = await $api.get(
      `${process.env.REACT_APP_BASE_URL}/calendar-events?isActiveSchedule=true&from=${date}&to=${date}`,
      {
        ...getOptions(),
      }
    );

    dispatch(schedulePageActions.setCalendarEvents(response.data));
  } catch (e) {
    console.log(e);
  }
};

export const getResourcesTimeoffsAndHolidays = ({ date, showAllLocations, isGridView, scheduleView } = {}) => async (dispatch) => {
  if (!date) return;
  const queryDate = format(new Date(date), globalBEDateFormat);

  try {
    const searchParams = new URLSearchParams();
    if (showAllLocations) searchParams.append('allLocations', true);
    if (isGridView) {
      searchParams.append('gridDate', queryDate);
    } else {
      searchParams.append('scheduleDate', queryDate);
    }
    if (scheduleView) {
      searchParams.append('view', scheduleView)
    }

    const response = await $api.get(
      `${process.env.REACT_APP_BASE_URL}/sheets/schedule/availability?${searchParams}`,
      {
        ...getOptions(),
      }
    );

    dispatch(schedulePageActions.setScheduleResourcesAvailability(response.data));
  } catch (e) {
    console.log(e);
  }
};

export const updateSelectedDate = (date) => (dispatch) => {
  dispatch(schedulePageActions.setSelectedDate(date));
  dispatch(getCalendarEvents(date));
};

export const addSubmittedSheet = (sheet) => (dispatch, getState) => {
  const { sheets } = getState().schedule;

  const adaptedData = sheets.map((s) =>
    s.sheet._id === sheet._id
      ? {
          ...s,
          sheet: sheet,
        }
      : s
  );
  dispatch(schedulePageActions.setSheets(adaptedData));
};

export const addNewSheet = (sheet) => (dispatch, getState) => {
  const { sheets } = getState().schedule;
  const timeFormat = getState().personalProfile.organization?.settings?.timeFormat;
  dispatch(schedulePageActions.setSheets([...sheets, ...adaptedDataHelper([sheet], timeFormat)]));
};

let sheetsRequestId = 0;

export const getSheets =
  ({ date, filters, isCrewLeader, isGridView, isMobile, showAllLocations }) =>
  async (dispatch, getState) => {
    const timeFormat = getState().personalProfile.organization?.settings?.timeFormat;
    const requestId = ++sheetsRequestId;

    dispatch(schedulePageActions.setLoading(true));
    try {
      const searchParams = new URLSearchParams();

      if (Object.keys(filters).length) {
        Object.entries(filters).map(([key, value]) => searchParams.append(key, value));
      }
      // isCrewLeader && searchParams.append('isCrewLeader', isCrewLeader);
      if (isGridView) {
        searchParams.append('gridDate', date);
      } else {
        searchParams.append('scheduleDate', date);
        if (isMobile) searchParams.append('sortBy', 'mobile');
      }

      if (showAllLocations) {
        searchParams.append('allLocations', true)
      }
      //! for mobile we need add param - sortBy='mobile';
      const response = await $api.get(
        `${process.env.REACT_APP_BASE_URL}/sheets/schedule?${searchParams}`,
        {
          ...getOptions(),
        }
      );

      // Overlapping requests (view/date switches) resolve out of order; only the latest may write.
      if (requestId !== sheetsRequestId) return;

      const adaptedData = adaptedDataHelper(response.data, timeFormat);

      if (!isCrewLeader) {
        dispatch(schedulePageActions.setSheets(adaptedData));
      } else {
        const clData = adaptedData.reduce((acc, el) => {
          const crewLeaders = el.workers.filter((worker) => worker.foreman);

          if (crewLeaders.length) {
            crewLeaders.forEach((worker) => {
              if (worker.hours?.length) {
                worker.hours.forEach((hours) => {
                  acc.push({
                    ...el,
                    cl: worker,
                    individualTime: hours,
                  });
                });
              } else {
                acc.push({ ...el, cl: worker });
              }
            });
          } else {
            acc.push(el);
          }
          return acc;
        }, []);
        dispatch(schedulePageActions.setSheets(clData));
      }
    } catch (e) {
      console.error(e);
    } finally {
      dispatch(schedulePageActions.setLoading(false));
    }
  };

export const getMonthSheets =
  ({ filters }) =>
  async (dispatch, getState) => {
    const { selectedDate } = getState().schedule;
    const { weekStart } = getState().personalProfile.organization.settings;
    dispatch(schedulePageActions.setLoading(true));

    try {
      const searchParams = new URLSearchParams();

      if (Object.keys(filters).length) {
        Object.entries(filters).map(([key, value]) => searchParams.append(key, value));
      }
      const { startOfWeekDay, endOfWeekDay } = getMonthDatesByFullWeeksAsKeys(
        selectedDate,
        weekStart === 'Sun' ? 0 : 1,
        new Date(),
        true
      );
      searchParams.append('from', startOfWeekDay.toISOString());
      searchParams.append('to', endOfWeekDay.toISOString());
      searchParams.append('groupBy', 'date');

      const response = await $api.get(
        `${process.env.REACT_APP_BASE_URL}/sheets/schedule?${searchParams}`,
        {
          ...getOptions(),
        }
      );

      const filteredData = structuredClone(response.data);
      Object.keys(filteredData).forEach(key => {
        filteredData[key] = filteredData[key]?.filter(sheet => {
          return format(new Date(sheet.hours?.start || sheet.startTimeDate || sheet.createdAt), globalBEDateFormat) === key;
        })
      })

      dispatch(schedulePageActions.setMonthSheets(filteredData));
    } catch (e) {
      console.error(e);
    }
    dispatch(schedulePageActions.setLoading(false));
  };

export const getWeekSheets = ({ filters }) => 
  async (dispatch, getState) => {
    const { selectedDate } = getState().schedule;
    dispatch(schedulePageActions.setLoading(true));

    try {
      const searchParams = new URLSearchParams();

      if (Object.keys(filters).length) {
        Object.entries(filters).map(([key, value]) => searchParams.append(key, value));
      }
      const startOfWeekDay = startOfDay(selectedDate);
      const endOfWeekDay = add(startOfWeekDay, { days: 7 });

      searchParams.append('from', startOfWeekDay.toISOString());
      searchParams.append('to', endOfWeekDay.toISOString());
      searchParams.append('groupBy', 'date');

      const response = await $api.get(
        `${process.env.REACT_APP_BASE_URL}/sheets/schedule?${searchParams}`,
        {
          ...getOptions(),
        }
      );

      const filteredData = structuredClone(response.data);
      Object.keys(filteredData).forEach(key => {
        filteredData[key] = filteredData[key]?.filter(sheet => {
          return format(new Date(sheet.hours?.start || sheet.startTimeDate || sheet.createdAt), globalBEDateFormat) === key;
        })
      })
      dispatch(schedulePageActions.setWeekSheets(filteredData));
    } catch (e) {
      console.error(e);
    }
    dispatch(schedulePageActions.setLoading(false));
  };

export const getEquipment = (options) => async (dispatch) => {
  dispatch(schedulePageActions.setLoading(true));

  const searchParams = new URLSearchParams();
  searchParams.append('isActiveSchedule', true);
  if (options?.allLocations) searchParams.append('allLocations', true)

  try {
    const response = await $api.get(
      `${process.env.REACT_APP_BASE_URL}/equipment?${searchParams}`,
      {
        ...getOptions(),
      }
    )

    dispatch(schedulePageActions.setResourcesEquipment(response.data));
  } catch (e) {
    console.log(e);
    dispatch(schedulePageActions.setResourcesEquipment([]));
  }
  dispatch(schedulePageActions.setLoading(false));
};

export const getWorkers = (options) => async (dispatch) => {
  dispatch(schedulePageActions.setLoading(true));

  const searchParams = new URLSearchParams();
  searchParams.append('activeSchedule', true);
  if (options?.allLocations) searchParams.append('allLocations', true)

  try {
    const response = await $api.get(`${process.env.REACT_APP_BASE_URL}/users?${searchParams}`, {
      ...getOptions(),
    });

    dispatch(schedulePageActions.setResourcesAllWorkers(response.data));
    dispatch(
      schedulePageActions.setResourcesWorkers(
        response.data?.filter((user) => user.profile.shifts.timeOfDay !== 'Inactive')
      )
    );
  } catch (e) {
    console.log(e);
    dispatch(schedulePageActions.setResourcesWorkers([]));
  }
  dispatch(schedulePageActions.setLoading(false));
};

export const reconcile =
  ({ viewType, gridDate, sheetIds, onError, date, onSuccess }) =>
  async (dispatch, getState) => {
    const data = {
      sheetIds,
    };

    if (date) {
      data.date = format(new Date(date), globalBEDateFormat);
    }
    if (gridDate) {
      data.gridDate = gridDate;
    }

    try {
      const response = await $api.post(
        `${process.env.REACT_APP_BASE_URL}/sheets/schedule/reconcile`,
        data,
        getOptions()
      );

      // dispatch(updateSheets());
      dispatch(schedulePageActions.reconcileWorklogs({ sheetIds: sheetIds }));
      onSuccess();
    } catch (e) {
      console.log(e);
      onError();
    }
  };

export const sendReconcileEmail =
  ({ isGridView, date, onError }) =>
  async (dispatch) => {
    try {
      const response = await $api.post(`${process.env.REACT_APP_BASE_URL}/emails/reconciledEmail`,
      { isGridView, date },
      {
        ...getOptions(),
      });

    } catch (e) {
      console.log(e);
      onError();
    }
  };

/**
 * 
 * @param {{ sheetIds: string[], notifyWorkerIds: string[] }} data 
 * @returns object
 */
export const publish = (data) => async (dispatch) => {
  try {
    const response = await $api.post(
      `${process.env.REACT_APP_BASE_URL}/sheets/schedule/publish
        `,
      data,
      {
        ...getOptions(),
      }
    );

    dispatch(
      schedulePageActions.publishWorklogs(data)
    );
    return response.data;
  } catch (e) {
    throw new Error(e?.response?.data?.message || e.message);
  }
};

/**
 *
 * @param {{ sheetIds: string[], notifyWorkerIds: string[] }} data
 * @returns object
 */
export const updatePublish = (data) => async (dispatch) => {
  try {
    const response = await $api.post(
      `${process.env.REACT_APP_BASE_URL}/sheets/schedule/update-publish
        `,
      data,
      {
        ...getOptions(),
      }
    );

    dispatch(
      schedulePageActions.publishWorklogs(data)
    );
    return response.data;
  } catch (e) {
    throw new Error(e?.response?.data?.message || e.message);
  }
};

export const updateSheetAddResource =
  (sheetId, isGenerateSheetObjects) => async (dispatch, getState) => {
    const { selectedDate } = getState().schedule;

    const searchParams = new URLSearchParams();
    searchParams.append('utcStartDate', startOfDay(selectedDate).toISOString());
    searchParams.append('utcEndDate', endOfDay(selectedDate).toISOString());
    searchParams.append('isGenerateSheetObjects', isGenerateSheetObjects);

    try {
      const response = await $api.get(
        `${process.env.REACT_APP_BASE_URL}/sheets/${sheetId}?${searchParams}`,
        {
          ...getOptions(),
        }
      )

      if (response?.data) {
        // Read sheets at dispatch time, not at thunk-start time, so that any
        // socket updates applied during the in-flight GET are not overwritten.
        const currentSheets = getState().schedule.sheets;
        dispatch(
          schedulePageActions.setSheets(
            currentSheets.map((s) =>
              s.sheet._id === sheetId
                ? adaptedDataHelper([response?.data])?.[0]
                : s
            )
          )
        );
      }
    } catch (e) {
      console.log(e);
    }
  };

export const updateSheets = () => async (dispatch, getState) => {
  const { selectedDate, filters, scheduleDayView, showAllLocations } = getState().schedule;
  const isCrewLeader = scheduleDayView === SCHEDULE_TYPES.cl;
  const date = format(selectedDate, globalBEDateFormat);
  const isGridView = scheduleDayView === SCHEDULE_TYPES.grid;
  const isMobile = isMobileSchedule();
  dispatch(
    getSheets({
      date,
      filters,
      isCrewLeader,
      isGridView,
      isMobile: isMobile,
      showAllLocations,
    })
  );
};

export const removeResources =
  ({ sheetId, workers, equipment }) =>
  async (dispatch, getState) => {
    const resourcesType = workers ? 'workers' : 'equipment';
    const resourceId = workers ? workers : equipment;

    try {
      const response = await $api.delete(
        `${process.env.REACT_APP_BASE_URL}/sheets/${sheetId}/${resourcesType}/${resourceId}
`,
        getOptions()
      );

      const sheets = getState().schedule.sheets;
      dispatch(
        schedulePageActions.setSheets(
          sheets
            // .filter((s) => !workerIds || !workerIds.includes(s.cl))
            .map((s) =>
              s._id === sheetId
                ? {
                    ...s,
                    workers: workers ? s.workers.filter((w) => w._id !== workers) : s.workers,
                    equipment: equipment
                      ? s.equipment.filter((w) => w._id !== equipment)
                      : s.equipment,
                    sheet: {
                      ...s.sheet,
                      workers: workers ? s.workers.filter((w) => w._id !== workers) : s.workers,
                      equipment: equipment
                        ? s.equipment.filter((w) => w._id !== equipment)
                        : s.equipment,
                    },
                  }
                : s
            )
        )
      );
    } catch (e) {
      console.log(e);
    }
  };


export const getFutureSheets = async ({ searchValue, skip, limit }) => {
  const from = startOfDay(new Date());

  const optionalParams = {
    searchValue,
    from,
    skip,
    limit,
    sortBy: 'contractor',
  };

  const options = {
    ...getOptions(),
    params: optionalParams,
  };

  try {
    const res = await $api.get(`${process.env.REACT_APP_BASE_URL}/sheets`, options);

    return { sheets: res?.data, hasMore: res?.data?.length === limit };
  } catch (e) {
    console.log(e);
  }
}

export const getResourceActionsBarSheets = async ({ searchValue, skip, limit, scheduleDates }) => {
  const params = new URLSearchParams();
  if (searchValue) params.append('searchValue', searchValue);
  params.append('skip', String(skip));
  params.append('limit', String(limit));
  params.append('sortBy', 'scheduleDay');
  (scheduleDates || []).forEach((d) => params.append('scheduleDates[]', d));

  try {
    const res = await $api.get(
      `${process.env.REACT_APP_BASE_URL}/sheets?${params.toString()}`,
      getOptions(),
    );
    return { sheets: res?.data, hasMore: res?.data?.length === limit };
  } catch (e) {
    console.log(e);
  }
}